/**
 * The Supabase client for play.runebound.gg (docs/design/07 §2): the shared Runebound project, its own
 * session on this origin (localStorage key `runebound.play.auth`; runebound.gg uses `runebound.auth`),
 * OAuth via redirect. `playDb()` targets the `play` schema (PostgREST `Accept-Profile: play`).
 */
import { type SupabaseClient, createClient } from '@supabase/supabase-js';
import { env, hasBackend } from './env';

/** localStorage key of the play session. */
export const AUTH_STORAGE_KEY = 'runebound.play.auth';

let client: SupabaseClient | null = null;

/** The shared client, or `null` when the build has no backend configured. */
export function getSupabase(): SupabaseClient | null {
  if (!hasBackend || env.supabaseUrl === null || env.supabaseKey === null) return null;
  client ??= createClient(env.supabaseUrl, env.supabaseKey, {
    auth: {
      storageKey: AUTH_STORAGE_KEY,
      persistSession: true,
      autoRefreshToken: true,
      // The hand-off route verifies its own token hash; OAuth redirects come back with `?code=` (PKCE).
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
  return client;
}

/** The client, throwing a clear error when the build has no backend (callers check `hasBackend` first). */
export function requireSupabase(): SupabaseClient {
  const sb = getSupabase();
  if (!sb) throw new Error('Runebound Play is not connected to its backend in this build.');
  return sb;
}

/** Queries against the `play` schema (RPCs `my_decks`, `load_deck`, `match_profiles`; tables `profile`, `invite`…). */
export function playDb() {
  return requireSupabase().schema('play');
}

/** The current access token, or `null` when signed out. */
export async function accessToken(): Promise<string | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data.session?.access_token ?? null;
}
